import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";

import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RecentChats from "src/views/assistant/components/splashScreen/RecentChats";
import { PageTopHelpButton } from "src/components/help";
import { NucToast } from "src/reusable/components/Toast";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import WorkflowSplashChatInput from "src/views/products/workflow/components/WorkflowSplashChatInput";
import { useWorkflowLeadPersona } from "src/views/products/workflow/hooks/useWorkflowLeadPersona";
import { useSetWorkflowRecentsConfig } from "src/views/products/workflow/hooks/useWorkflowProductConfig";
import WorkflowPersonaSlotsPanel from "src/views/products/workflow/components/WorkflowPersonaSlotsPanel";
import { spaNavigate } from "src/views/products/workflow/routeLink";
import { isInternalUser } from "src/helpers/internalUser";
import { FIFTY_FIFTY_NINE_PRODUCT } from "./fiftyFiftyNineProduct";
import FfnPlantCard from "./components/FfnPlantCard";
import FfnActivityCard from "./components/FfnActivityCard";
import { createFfnRecord, useFfnPlants } from "./services/FfnService";
import { useFfnStore } from "./store/FfnStore";

/**
 * 50.59 splash: pick the plant, describe the activity, kick off with the
 * prefilled chat box. Send is not a stream send — it creates the dialogue
 * (with the 50.59 product slug, so the workflow tools and context injection
 * activate), creates the record linked to that dialogue (the record-side
 * mirror of PG's create-run), then navigates; the chat page fires the first
 * turn on mount.
 */
const FfnSplashPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const {
    config: chatConfig,
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    resetToBaseConfig,
    isChatReady,
  } = useChatStore();

  const ffnReset = useFfnStore((s) => s.reset);
  const { selectedPlantId, activityTitle, activityDescription } = useFfnStore();
  const { data: plants = [] } = useFfnPlants();
  const { mutateAsync: createDialogue, isLoading: isCreatingDialogue } =
    useCreateDialogue();

  useSetWorkflowRecentsConfig(FIFTY_FIFTY_NINE_PRODUCT, queryClient);

  // Reset chat + run state on mount so nothing leaks in from another product,
  // then let the lead persona auto-select into the emptied store.
  useEffect(() => {
    setActiveDialogueId(null);
    setInitialUserMessage(undefined);
    clearUploadedFiles();
    resetToBaseConfig();
    ffnReset();
  }, [
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    resetToBaseConfig,
    ffnReset,
  ]);

  const { declaration } = useWorkflowLeadPersona(FIFTY_FIFTY_NINE_PRODUCT);

  const selectedPlant = useMemo(
    () => plants.find((p) => p.id === selectedPlantId) ?? null,
    [plants, selectedPlantId],
  );

  // The record itself needs no mention: the context injector surfaces its
  // state (plant, activity, status) to the lead from the first turn.
  const defaultPrompt =
    "Review the proposed activity, then work it through applicability, " +
    "screening, and evaluation.";

  const handleKickoff = async (text: string): Promise<boolean> => {
    if (!selectedPlantId) {
      NucToast({ message: "Select a plant first.", type: "error" });
      return false;
    }
    if (!activityTitle.trim() || !activityDescription.trim()) {
      NucToast({
        message: "Describe the proposed activity (title and description).",
        type: "error",
      });
      return false;
    }
    if (!isChatReady()) {
      NucToast({
        message: "Chat is not ready. Please wait for configuration to load.",
        type: "error",
      });
      return false;
    }

    try {
      const dialogueId = await useFfnStore
        .getState()
        .getOrCreateDialogue(async () =>
          createDialogue({
            conversation: {
              model_id: chatConfig?.modelId ?? null,
              model_version_number: chatConfig?.modelVersionNum ?? null,
              model_inference_data: {
                infer_data: JSON.stringify([]),
                stream: true,
                streaming_response_required: true,
                text_gen_model_params: chatConfig?.chatControlMetadata || {},
              },
              search_datasets: chatConfig?.datasets?.map((d) => d.id) || [],
              n_search_results: chatConfig?.topSearchResults || 5,
              tool_calling: true,
              chat_config_id: chatConfig?.chatConfigId ?? null,
              product: FIFTY_FIFTY_NINE_PRODUCT.productKey,
            },
          }),
        );
      await createFfnRecord({
        plant_id: selectedPlantId,
        title: activityTitle.trim(),
        activity_description: activityDescription.trim(),
        dialogue_id: dialogueId,
      });
      setInitialUserMessage(text);
      history.push(`${FIFTY_FIFTY_NINE_PRODUCT.basePath}/chat/${dialogueId}`);
      return true;
    } catch (error: any) {
      console.error("[FfnSplashPage] Kickoff failed:", error);
      NucToast({
        message:
          error?.response?.data?.detail ||
          "Failed to start the 50.59 review. Please try again.",
        type: "error",
      });
      return false;
    }
  };

  return (
    <Box
      sx={{
        height: "inherit",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 3,
        overflowY: "auto",
      }}
    >
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            mb: 1,
          }}
        >
          {/* Records is Nuclearn-only; the route bounces customers, so
              the button that leads there stays hidden for them too. */}
          {isInternalUser() && (
            <Button
              // href + onClick, not component={RouterLink}: MUI's polymorphism
              // does not typecheck against the nested @types/react that
              // @types/react-router pulls in.
              href={`${FIFTY_FIFTY_NINE_PRODUCT.basePath}/records`}
              onClick={spaNavigate(() =>
                history.push(`${FIFTY_FIFTY_NINE_PRODUCT.basePath}/records`),
              )}
              size="small"
              startIcon={<FactCheckOutlinedIcon />}
              sx={{ mr: "auto", textTransform: "none" }}
              data-testid="ffn-btn-records"
            >
              Records
            </Button>
          )}
          <WorkflowPersonaSlotsPanel product={FIFTY_FIFTY_NINE_PRODUCT} />
          <PageTopHelpButton
            productLabel="50.59 Evaluation"
            product={FIFTY_FIFTY_NINE_PRODUCT.productKey}
          />
        </Box>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", mb: 2 }}
          flexWrap="wrap"
        >
          <img
            src="/images/nuclearn-n-full.png"
            alt="Nuclearn Logo"
            width={40}
            height={40}
          />
          <Typography
            component="h1"
            data-testid="ffn-page-heading"
            sx={{ fontFamily: "Poppins", fontSize: "36px" }}
          >
            50.59 Evaluation
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Select a plant, describe the proposed activity, and the 50.59
          Evaluation Lead will work the record through applicability, screening,
          and evaluation.
        </Typography>
      </Box>

      {!selectedPlant?.reference_documents?.length && selectedPlant && (
        <Box sx={{ width: "100%", maxWidth: 900, mb: 2 }}>
          <Alert severity="warning">
            {selectedPlant.name} has no reference documents linked. The agent
            needs at least a UFSAR dataset to screen against.
          </Alert>
        </Box>
      )}

      <Stack spacing={3} sx={{ width: "100%", maxWidth: 900, mb: 3 }}>
        <FfnPlantCard referenceRoles={declaration?.reference_roles ?? []} />
        <FfnActivityCard />
      </Stack>

      <Box sx={{ width: "100%", maxWidth: 900 }}>
        <WorkflowSplashChatInput
          defaultValue={defaultPrompt}
          sendUserMessage={handleKickoff}
          isChatLoading={isCreatingDialogue}
          placeholder="Describe what you want the 50.59 Evaluation Lead to do…"
          product={FIFTY_FIFTY_NINE_PRODUCT.productKey}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 3,
        }}
      >
        <RecentChats productKey={FIFTY_FIFTY_NINE_PRODUCT.productKey} />
      </Box>
    </Box>
  );
};

export default FfnSplashPage;
